import React, { useState, useEffect } from 'react';
import { TextField, MenuItem, FormGroup, Tooltip, IconButton, Stack, TextFieldProps, Button } from '@mui/material';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import TypingIndicator from "./TypingIndicator";
import ClearIcon from '@mui/icons-material/Clear';
import { Box } from '@mui/system';

export const EditableTextField = (props: { placeholderElement?: JSX.Element, onAiSuggest?: () => void, endAdornment?: JSX.Element, onSave: (value: string) => void } & TextFieldProps) => {
  const [isSuggesting, shouldSuggest] = useState<boolean>(false);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [value, setValue] = useState<string>(props.defaultValue ?? "");
  const [hasFocus, setHasFocus] = useState<boolean>(props.autoFocus ?? false);

  useEffect(() => {
    if (!isSuggesting) return;
    props.onAiSuggest?.();
  }, [isSuggesting]);

  useEffect(() => {
    shouldSuggest(false);
  }, [props.value]);

  return isEditing ? <Stack sx={{width: '100%'}} spacing={1}>
    <TextField
      multiline={Number(props.rows ?? 3) > 1}
      fullWidth
      rows={props.rows ?? 3}
      variant="filled"
      {...props}
      autoFocus={hasFocus}
      onChange={e => setValue(e.currentTarget.value)}
      sx={{[`.MuiFilledInput-input`] : { p: 0 }, [`.MuiFilledInput-root`]: {p: 1.5}}}
      InputProps={(props.onAiSuggest || props.endAdornment) && {
        sx: {alignItems: 'flex-start'},
        endAdornment: props.endAdornment ?? <>{ isSuggesting ? <TypingIndicator /> : 
          <Stack>
            <Tooltip title="Let AI suggest">
              <IconButton onClick={shouldSuggest} disabled={props.disabled}>
                <AutoFixHighIcon fontSize='small' />
              </IconButton>
            </Tooltip>
            { props.value && <Tooltip title="Clear input">
              <IconButton onClick={() => {
                props.onChange("");
                props.onSave("");
              }}>
                <ClearIcon fontSize='small' />
              </IconButton></Tooltip> }
          </Stack> }</>
      }}
    /> 
    <Stack direction="row" justifyContent="flex-end" spacing={1}>
      <Button variant="outlined" color="secondary" size="small" onClick={() => setIsEditing(false)}>Cancel</Button>
      <Button variant="outlined" size="small" color="primary" onClick={() => {
        props.onSave(value ?? "");
        setIsEditing(false);
      }}>Save</Button>
    </Stack>
  </Stack> : <Box sx={{p: 1, mx: -1, cursor: 'pointer', borderRadius: 'var(--border-radius-item)', [`&:hover`]: {bgcolor: 'var(--color-greyDark)'}}} onClick={() => {
    setIsEditing(true);
    setHasFocus(true);
  }}>{props.placeholderElement ?? <span>{props.defaultValue}</span>}</Box>;
};

export const PasswordForm = (props) => {

  const [passwordHasError, setPasswordHasError] = useState(false);
  const [passwordConfirmationHasError, setPasswordConfirmationHasError] = useState(false);

  const checkPasswords = (event) => {
    let password = document.getElementById("password")?.value;
    let passwordConfirmation = document.getElementById("password-confirmation")?.value;
    setPasswordHasError(password.length < 8);
    setPasswordConfirmationHasError(password != passwordConfirmation);
    if (props.onChange !== undefined) { props.onChange() }
  }

  return(<>
    <FormGroup>
      <TextField 
        id="password" 
        type="password" 
        name="password"
        error={passwordHasError}
        required
        helperText="Min. 8 characters."
        label="New Password"
        variant="filled"
        onChange={checkPasswords}
        sx={{marginBottom: '0.15rem'}} />

      <TextField 
        id="password-confirmation"
        type="password"
        name="password_confirmation" 
        error={passwordConfirmationHasError} 
        required 
        helperText="Passwords must match." 
        label="Repeat Password" 
        variant="filled" onChange={checkPasswords} />
    </FormGroup>
  </>);
}

export const ItemSelector = (props) => {
  
  return (
    <TextField
      label={props.label} defaultValue={props.defaultValue}
      select
      variant="filled"
      onChange={props.onChange}
      required={props.required}
    >
      {props.items?.map(item => (
        <MenuItem key={item.value} value={item.value}>
          {item.label}
        </MenuItem>
      ))}
    </TextField>
  );
}